import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TOTAL_TICKETS } from "../utils/formatters";
import { STATUS } from "../constants/colors";

const STATUS_LABEL = {
  available: "Available",
  reserved: "Reserved",
  sold: "Sold",
  funds_received: "Paid",
};

// Tiny child row that registers itself with the shared IntersectionObserver so the
// phone is only fetched once the row is actually scrolled into view.
const RenewalRow = ({ n, t, phone, onObserve, onMarkCalled, onOpenTicket, currentUser, isAdmin }) => {
  const rowRef = useRef(null);
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    onObserve(el, n);
    return () => onObserve(el, n, true);
  }, [n, onObserve]);

  const meta = STATUS[t.status] || STATUS.available;
  const statusLabel = STATUS_LABEL[t.status] || t.status;
  const canModify = isAdmin || t.assigned_seller_id === currentUser?.id;
  const [noteDraft, setNoteDraft] = useState(t.called_note || "");
  const [savingNote, setSavingNote] = useState(false);
  // Optimistic call state so the checkbox feels instant — synced from server prop afterward.
  const [optimisticCalled, setOptimisticCalled] = useState(t.called);
  useEffect(() => { setOptimisticCalled(t.called); }, [t.called]);

  useEffect(() => { setNoteDraft(t.called_note || ""); }, [t.called_note]);

  const toggleCalled = async () => {
    const next = !optimisticCalled;
    setOptimisticCalled(next);
    const res = await onMarkCalled(n, { called: next, note: noteDraft });
    if (!res.ok) setOptimisticCalled(!next); // rollback on failure
  };

  const saveNote = async () => {
    if (!optimisticCalled) return;
    if ((noteDraft || "") === (t.called_note || "")) return;
    setSavingNote(true);
    await onMarkCalled(n, { called: true, note: noteDraft });
    setSavingNote(false);
  };

  return (
    <tr ref={rowRef} className="border-t" style={{ borderColor: "var(--border-default)", background: optimisticCalled ? "var(--semantic-success-soft)" : "transparent" }} data-testid={`renewals-row-${n}`}>
      <td className="px-3 py-2 print:hidden text-center">
        {canModify ? (
          <input
            type="checkbox"
            checked={optimisticCalled}
            onChange={toggleCalled}
            data-testid={`renewals-called-${n}`}
            aria-label={`Mark ticket ${n} called`}
          />
        ) : optimisticCalled ? (
          <span className="text-xs" style={{ color: "var(--semantic-success)" }} title={`Called by ${t.called_by_name}`}>✓</span>
        ) : null}
      </td>
      <td className="px-3 py-2 font-semibold">#{n}</td>
      <td className="px-3 py-2">
        <div>{t.prior_buyer_name}</div>
        <div className="text-xs" style={{ color: "var(--ink-tertiary)" }}>Last bought {t.prior_buyer_year}</div>
      </td>
      <td className="px-3 py-2">
        {(() => {
          // Prefer the phone already on the ticket (admin / viewer / owner see it directly).
          // Fall back to the lazily-fetched cached value for any leftover cases.
          const directPhone = t.prior_phone;
          const resolved = directPhone || phone;
          if (resolved) {
            return (
              <a href={`tel:${resolved}`} className="font-medium" style={{ color: "var(--brand-primary)" }} data-testid={`renewals-phone-${n}`}>
                {resolved}
              </a>
            );
          }
          if (phone === undefined && !directPhone) {
            return <span className="text-xs" style={{ color: "var(--ink-tertiary)" }}>…</span>;
          }
          return <span className="text-xs" style={{ color: "var(--ink-tertiary)" }}>—</span>;
        })()}
      </td>
      <td className="px-3 py-2" style={{ color: "var(--ink-tertiary)" }}>{t.assigned_seller_name || "—"}</td>
      <td className="px-3 py-2">
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: meta.bg, color: meta.text, boxShadow: `inset 0 0 0 1px ${meta.outline}` }}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-3 py-2 print:hidden">
        {canModify && optimisticCalled && (
          <input
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={saveNote}
            placeholder="Note (optional)"
            data-testid={`renewals-note-${n}`}
            className="w-full rounded-md border bg-[color:var(--surface-card)] px-2 py-1 text-xs"
            style={{ borderColor: savingNote ? "var(--brand-primary)" : "var(--border-default)" }}
          />
        )}
        {optimisticCalled && !canModify && t.called_note && (
          <span className="text-xs" style={{ color: "var(--ink-tertiary)" }}>{t.called_note}</span>
        )}
        {optimisticCalled && t.called_by_name && (
          <div className="text-[10px] mt-0.5" style={{ color: "var(--semantic-success)" }}>by {t.called_by_name}</div>
        )}
      </td>
      <td className="px-3 py-2 print:hidden">
        {t.status === "available" && canModify && (
          <button
            type="button"
            onClick={() => onOpenTicket(n)}
            data-testid={`renewals-renew-${n}`}
            className="px-3 py-1 text-xs font-medium text-white rounded-md hover:opacity-90"
            style={{ background: "var(--semantic-success)" }}
          >
            Renew
          </button>
        )}
      </td>
    </tr>
  );
};

export const RenewalsTab = ({ tickets, currentUser, getTicketHistory, onMarkCalled, onOpenTicket }) => {
  const isAdmin = currentUser?.role === "admin";
  const isSeller = currentUser?.role === "seller";
  const [scope, setScope] = useState(isSeller ? "mine" : "all"); // mine | all
  const [statusFilter, setStatusFilter] = useState("available"); // available | all | uncalled
  const [search, setSearch] = useState("");
  const [phoneCache, setPhoneCache] = useState({});
  // Observer + queue for lazy phone fetch
  const queueRef = useRef(new Set());
  const flushTimerRef = useRef(null);

  const baseList = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= TOTAL_TICKETS; i++) {
      const t = tickets[i];
      if (!t) continue;
      if (!t.has_prior_buyer) continue;
      if (scope === "mine" && t.assigned_seller_id !== currentUser?.id) continue;
      if (statusFilter === "available" && t.status !== "available") continue;
      if (statusFilter === "uncalled" && (t.status !== "available" || t.called)) continue;
      arr.push({ n: i, t });
    }
    return arr;
  }, [tickets, scope, statusFilter, currentUser]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseList;
    return baseList.filter(({ n, t }) =>
      String(n).includes(q) ||
      (t.prior_buyer_name || "").toLowerCase().includes(q) ||
      (t.assigned_seller_name || "").toLowerCase().includes(q)
    );
  }, [baseList, search]);

  const counts = useMemo(() => {
    let toCall = 0;
    let called = 0;
    let renewed = 0;
    baseList.forEach(({ t }) => {
      if (t.status !== "available") renewed++;
      else if (t.called) called++;
      else toCall++;
    });
    return { toCall, called, renewed, total: baseList.length };
  }, [baseList]);

  // Flush queued phone fetches in parallel batches.
  const flushFetchQueue = useCallback(async () => {
    flushTimerRef.current = null;
    const nums = Array.from(queueRef.current).filter((n) => phoneCache[n] === undefined);
    queueRef.current.clear();
    if (nums.length === 0 || !getTicketHistory) return;
    const BATCH = 10;
    for (let i = 0; i < nums.length; i += BATCH) {
      const chunk = nums.slice(i, i + BATCH);
      const results = await Promise.all(
        chunk.map((n) => getTicketHistory(n).then((res) => [n, res?.history?.[0]?.phone || ""]).catch(() => [n, ""]))
      );
      setPhoneCache((prev) => {
        const next = { ...prev };
        results.forEach(([n, phone]) => { next[n] = phone; });
        return next;
      });
    }
  }, [getTicketHistory, phoneCache]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => flushFetchQueue(), 150);
  }, [flushFetchQueue]);

  // Build an IntersectionObserver to observe row elements
  const observerRef = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    observerRef.current = new IntersectionObserver((entries) => {
      let queued = false;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const n = Number(entry.target.getAttribute("data-row-n"));
        if (!n || phoneCache[n] !== undefined) return;
        queueRef.current.add(n);
        queued = true;
      });
      if (queued) scheduleFlush();
    }, { rootMargin: "200px 0px" }); // pre-load just below the viewport
    return () => observerRef.current?.disconnect();
  }, [phoneCache, scheduleFlush]);

  // Imperative observe/unobserve method passed to each row
  const onObserveRow = useCallback((el, n, remove = false) => {
    if (!el || !observerRef.current) return;
    el.setAttribute("data-row-n", String(n));
    if (remove) observerRef.current.unobserve(el);
    else observerRef.current.observe(el);
  }, []);

  const printList = () => window.print();

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 print:p-0">
      <div className="bg-[color:var(--surface-card)] border rounded-lg p-5 print:border-0 print:p-0" style={{ borderColor: "var(--border-default)" }} data-testid="renewals-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4 print:hidden">
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>
              {isSeller && scope === "mine" ? "My Returning Buyers" : "Returning Buyers"}
            </h2>
            <p className="text-sm mt-1" style={{ color: "var(--ink-tertiary)" }}>
              Past buyers who haven&apos;t renewed yet — give them first dibs on their usual ticket. Check off each call as you make it so you can pick up where you left off.
            </p>
          </div>
          <button
            type="button"
            onClick={printList}
            data-testid="renewals-print"
            className="px-4 py-2 text-sm font-medium rounded-md border bg-[color:var(--surface-card)] hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)] transition-colors"
            style={{ borderColor: "var(--border-default)", color: "var(--ink-tertiary)" }}
          >
            Print list
          </button>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block mb-4">
          <h1 className="text-xl font-bold">Returning Buyer Call List</h1>
          <p className="text-sm">
            {isSeller && scope === "mine" ? `Seller: ${currentUser?.name}` : "All sellers"}
            {" · Printed "}
            {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 print:hidden">
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>To call</p>
            <p className="text-xl font-semibold" style={{ color: "var(--status-sold-bg)" }} data-testid="renewals-to-call">{counts.toCall}</p>
          </div>
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Called</p>
            <p className="text-xl font-semibold" style={{ color: "var(--semantic-success)" }} data-testid="renewals-called-stat">{counts.called}</p>
          </div>
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Renewed</p>
            <p className="text-xl font-semibold" style={{ color: STATUS.funds_received.bg }} data-testid="renewals-renewed">{counts.renewed}</p>
          </div>
          <div className="border rounded-md p-3" style={{ borderColor: "var(--border-default)" }}>
            <p className="text-xs uppercase tracking-wide" style={{ color: "var(--ink-tertiary)" }}>Total returning</p>
            <p className="text-xl font-semibold" style={{ color: "var(--ink-primary)" }}>{counts.total}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3 print:hidden">
          {isSeller && (
            <div className="inline-flex rounded-md border" style={{ borderColor: "var(--border-default)" }}>
              <button
                onClick={() => setScope("mine")}
                data-testid="renewals-scope-mine"
                className={`px-3 py-1.5 text-xs font-medium ${scope === "mine" ? "bg-[color:var(--ink-primary)] text-white" : "bg-[color:var(--surface-card)] text-[color:var(--ink-tertiary)]"}`}
              >
                My tickets
              </button>
              <button
                onClick={() => setScope("all")}
                data-testid="renewals-scope-all"
                className={`px-3 py-1.5 text-xs font-medium border-l ${scope === "all" ? "bg-[color:var(--ink-primary)] text-white" : "bg-[color:var(--surface-card)] text-[color:var(--ink-tertiary)]"}`}
                style={{ borderColor: "var(--border-default)" }}
              >
                All sellers
              </button>
            </div>
          )}
          <div className="inline-flex rounded-md border" style={{ borderColor: "var(--border-default)" }}>
            <button
              onClick={() => setStatusFilter("uncalled")}
              data-testid="renewals-filter-uncalled"
              className={`px-3 py-1.5 text-xs font-medium ${statusFilter === "uncalled" ? "bg-[color:var(--ink-primary)] text-white" : "bg-[color:var(--surface-card)] text-[color:var(--ink-tertiary)]"}`}
            >
              Not yet called
            </button>
            <button
              onClick={() => setStatusFilter("available")}
              data-testid="renewals-filter-tocall"
              className={`px-3 py-1.5 text-xs font-medium border-l ${statusFilter === "available" ? "bg-[color:var(--ink-primary)] text-white" : "bg-[color:var(--surface-card)] text-[color:var(--ink-tertiary)]"}`}
              style={{ borderColor: "var(--border-default)" }}
            >
              To call (not renewed)
            </button>
            <button
              onClick={() => setStatusFilter("all")}
              data-testid="renewals-filter-all"
              className={`px-3 py-1.5 text-xs font-medium border-l ${statusFilter === "all" ? "bg-[color:var(--ink-primary)] text-white" : "bg-[color:var(--surface-card)] text-[color:var(--ink-tertiary)]"}`}
              style={{ borderColor: "var(--border-default)" }}
            >
              All returning
            </button>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ticket #, buyer, or seller…"
            data-testid="renewals-search"
            className="flex-1 min-w-[200px] rounded-md border bg-[color:var(--surface-card)] px-3 py-1.5 text-sm focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25"
            style={{ borderColor: "var(--border-default)" }}
          />
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: "var(--ink-tertiary)" }} data-testid="renewals-empty">
            {counts.total === 0
              ? "No returning buyers in this scope yet."
              : "Nothing matches the current filter. Try widening it or clearing your search."}
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--border-default)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--surface-page)" }}>
                <tr style={{ color: "var(--ink-tertiary)" }}>
                  <th className="px-3 py-2 font-medium w-10 print:hidden">Called?</th>
                  <th className="text-left px-3 py-2 font-medium">Ticket</th>
                  <th className="text-left px-3 py-2 font-medium">Buyer (last year)</th>
                  <th className="text-left px-3 py-2 font-medium">Phone</th>
                  <th className="text-left px-3 py-2 font-medium">Seller</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium print:hidden">Note</th>
                  <th className="text-left px-3 py-2 font-medium print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(({ n, t }) => (
                  <RenewalRow
                    key={n}
                    n={n}
                    t={t}
                    phone={phoneCache[n]}
                    onObserve={onObserveRow}
                    onMarkCalled={onMarkCalled}
                    onOpenTicket={onOpenTicket}
                    currentUser={currentUser}
                    isAdmin={isAdmin}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};
